import React, { useState } from 'react';
import useCreateTitle from './useCreateTitle';
import { useTranslation } from 'react-i18next';
import api from '../services/api';
import { catAttachedToBand, filterItemsForStorage } from '../config/categoryConstants';
import { buildCapabilityLookup, opAllowsCat } from '../utils/capabilities';
import { ItemPick, StorageBandPick, BandPick } from '../components/FormPickers';
import { today, isFutureDt, MSG_ERR, emptyRow, storagesForOp, useApi } from './createFormUtils';
import { IcoLn } from './ActionIcons';
import { Emo, emoText } from './EmojiIcons';
import DrawerHeader from './drawer/DrawerHeader';
import DrawerFooter from './drawer/DrawerFooter';
import useActionLabel from '../i18n/useActionLabel';
import './createFormBodies.css';
import { TextInput } from '../components/inputs/Input';
import ReferenceField from '../components/inputs/ReferenceField';
import { useQtySanitizer } from '../hooks/useItemQtyDecimals';

// fb-37 — Record production, the FIRST fully-wired create drawer (depth-first). Layout follows the mock
// (.cform-root classes); data + behavior are ported from the legacy ProductionForm (DataEntryPage.jsx:578)
// and the multi-row model from ConsumptionMultiRow (:3899): real dropdowns via the capability matrix,
// single POST /production, and multi-row (oneBand → /band-batch validate+commit · per-row → N× /production).
// Planned status = future date OR "Save as Planned" (period-lock ctx lives inside DataEntryPage, not here).

export default function ProductionCreateForm({ size, onSize, onFullPage, onClose, onInteract }) {
  const al = useActionLabel();
  const ct = useCreateTitle();
  const { t } = useTranslation('dataEntry');
  const master = useApi({ bands: () => api.getBioOpBands(), items: () => api.getEntryItems(), storages: () => api.getStorages(), capabilities: () => api.getOperationCategoryRules() });
  const cap = buildCapabilityLookup(master.capabilities);
  const items = master.items || [], bands = master.bands || [], storages = master.storages || [];
  const productionItems = items.filter(i => opAllowsCat(cap, 'Production', i.category));

  // shared UI state
  const [multiRow, setMultiRow] = useState(false);
  const [oneBand, setOneBand] = useState(true);
  const [busy, setBusy] = useState(false);
  const [msg, setMsg] = useState('');
  const [err, setErr] = useState(false);
  const touch = () => { if (onInteract) onInteract(); };
  // fb-177: after a successful save the form is back to empty -> not an in-progress draft any more.
  const clean = () => { if (onInteract) onInteract(false); };
  const say = (m, isErr) => { setMsg(m); setErr(!!isErr); };
  const fail = m => say(MSG_ERR + t('msg.errorPrefix', { defaultValue: '' }) + m, true);

  // single-row state
  const [form, setForm] = useState({ date: today(), band_id: '', item_id: '', qty: '', storage_id: '', notes: '', reference_id: '' });
  const sq = useQtySanitizer();   // W13 (GES-388): quantity typed at the item's own decimals
  const set = k => e => { setForm(p => ({ ...p, [k]: e.target.value })); touch(); };
  const selItem = items.find(i => i.id === form.item_id);
  const needsBand = selItem ? catAttachedToBand(selItem.category) : true;
  const pickItem = e => {
    const v = e.target.value, it = items.find(i => i.id === v);
    setForm(p => ({ ...p, item_id: v, band_id: catAttachedToBand(it && it.category) ? p.band_id : '' }));
    touch();
  };

  // multi-row state
  const [mDate, setMDate] = useState(today());
  const [mBand, setMBand] = useState('');
  const [rows, setRows] = useState([emptyRow()]);
  const [vWarn, setVWarn] = useState(null);      // band-batch validate verdict==='warn' payload
  const [ack, setAck] = useState(false);
  const patch = (i, p) => { setRows(rs => rs.map((r, x) => x === i ? { ...r, ...p } : r)); setVWarn(null); setAck(false); touch(); };
  const addRow = () => { setRows(rs => { const last = rs[rs.length - 1], seed = emptyRow(); if (!oneBand && last && last.band_id) seed.band_id = last.band_id; return [...rs, seed]; }); touch(); };
  const dropRow = i => { setRows(rs => rs.filter((_, x) => x !== i)); touch(); };
  const rowLive = r => !!(r.item_id || r.qty);

  const plannedFor = (date, force) => isFutureDt(date) || force;

  const singleMissing = () => {
    const m = [];
    if (!form.item_id) m.push('item');
    if (!form.qty || parseFloat(form.qty) <= 0) m.push('quantity');
    if (!form.storage_id) m.push('storage');
    if (needsBand && !form.band_id) m.push('band');
    return m;
  };
  async function saveSingle(force, keep) {
    const miss = singleMissing();
    if (miss.length) return fail(t('validation.rowIncomplete', { defaultValue: 'Missing required: ' + miss.join(', ') + '.' }) + ' (' + miss.join(', ') + ')');
    const planned = plannedFor(form.date, force);
    setBusy(true); say('');
    try {
      await api.createProduction({ ...form, qty: parseFloat(form.qty), tx_status: planned ? 'planned' : 'posted' });
      say(t('production.saved', { defaultValue: 'Production saved.' })); if (!keep) clean();
      setForm(keep ? { ...form, qty: '', notes: '', reference_id: '' } : { date: today(), band_id: '', item_id: '', qty: '', storage_id: '', notes: '', reference_id: '' });
    } catch (e) { fail(e.message); } finally { setBusy(false); }
  }

  async function saveMulti(force, keep) {
    const live = rows.filter(rowLive);
    if (!live.length) return fail(t('validation.nothingToSave', { defaultValue: 'Nothing to save.' }));
    if (oneBand && !mBand) return fail(t('validation.multiRowBandRequired', { defaultValue: 'Pick a band for the batch.' }));
    const bad = live.findIndex(r => !r.item_id || !r.qty || parseFloat(r.qty) <= 0 || !r.storage_id);
    if (bad >= 0) { const r = live[bad], miss = [!r.item_id && 'item', (!r.qty || parseFloat(r.qty) <= 0) && 'quantity', !r.storage_id && 'storage'].filter(Boolean); return fail(t('validation.rowIncomplete', { defaultValue: 'Every row needs item, quantity and storage.' }) + ` (${live.length > 1 ? `row ${bad + 1}: ` : ''}${miss.join(', ')})`); }
    if (!oneBand && live.some(r => !r.band_id)) return fail(t('validation.multiRowRowBandRequired', { defaultValue: 'Every row needs a band.' }));
    const planned = plannedFor(mDate, force);
    setBusy(true); say('');
    try {
      if (oneBand) {
        const payload = { date: mDate, band_id: mBand, tx_status: planned ? 'planned' : 'posted',
          production: live.map(r => ({ item_id: r.item_id, qty: parseFloat(r.qty), storage_id: r.storage_id, ...(r.notes && { notes: r.notes }) })), consumption: [], mortality: [] };
        if (!planned) {
          const fresh = await api.bandBatchValidate(payload);
          if (fresh && fresh.verdict === 'block') { setBusy(false); return fail(t('validation.fixBlockedRows', { defaultValue: 'Fix the blocked rows first.' })); }
          if (fresh && fresh.verdict === 'warn' && !ack) { setVWarn(fresh); setBusy(false); return fail(t('validation.acknowledgeFirst', { defaultValue: 'Acknowledge the warnings first.' })); }
        }
        await api.bandBatchCommit({ ...payload, acknowledge_warnings: ack });
        say(t('multiRow.savedAll', { count: live.length, defaultValue: `Saved ${live.length} rows.` }));
      } else {
        let ok = 0, failed = 0, next = rows.map(r => ({ ...r }));
        for (let i = 0; i < next.length; i++) {
          const r = next[i]; if (!rowLive(r) || (r._status && r._status.state === 'ok')) continue;
          try { await api.createProduction({ date: mDate, band_id: r.band_id, item_id: r.item_id, qty: parseFloat(r.qty), storage_id: r.storage_id, ...(r.notes && { notes: r.notes }), tx_status: planned ? 'planned' : 'posted' }); r._status = { state: 'ok' }; ok++; }
          catch (e) { r._status = { state: 'fail', message: e.message }; failed++; }
        }
        setRows(next);
        if (failed) { say(MSG_ERR + t('multiRow.partialSummary', { ok, failed, defaultValue: `${ok} saved, ${failed} failed.` }), true); setBusy(false); return; }
        say(t('multiRow.savedAll', { count: ok, defaultValue: `Saved ${ok} rows.` }));
      }
      const kept = rows.filter(rowLive).map(r => ({ ...r, qty: '', notes: '', _status: null }));   // fb-157: keep item / storage
      setRows(keep && kept.length ? kept : [emptyRow()]); setVWarn(null); setAck(false);
      if (!keep) { setMDate(today()); setMBand(''); clean(); }
    } catch (e) { fail(e.message); } finally { setBusy(false); }
  }

  const doSave = (force, keep) => (multiRow ? saveMulti : saveSingle)(force, keep);
  const clearForm = () => {
    setForm({ date: today(), band_id: '', item_id: '', qty: '', storage_id: '', notes: '', reference_id: '' });
    setRows([emptyRow()]); setMDate(today()); setMBand(''); setVWarn(null); setAck(false); say(''); touch();
  };
  const headerPlanned = multiRow ? isFutureDt(mDate) : isFutureDt(form.date);
  const invalid = !multiRow && singleMissing().length > 0;   // single-mode required-field gate (multi validates on save)

  // ---- render helpers (mock .field markup) ----
  const ev = v => ({ target: { value: v } });   // the pickers hand back the value; the form's handlers read e.target.value
  const itemSelect = (value, onChange, storageId) => (
    <ItemPick items={filterItemsForStorage(productionItems, storages, storageId)} value={value} onChange={v => onChange(ev(v))} required placeholder={t('select.item', { defaultValue: 'Select item…' })} />
  );
  const storageSelect = (value, onChange, itemId) => (
    <StorageBandPick storages={storagesForOp(storages, items, itemId, cap, 'Production')} bands={[]} value={value} onChange={v => onChange(ev(v))} required placeholder={t('select.storage', { defaultValue: 'Select storage…' })} />
  );

  return (
    <>
      <DrawerHeader eyebrow={t('dataEntry:fl.operations_new_record')} ini={<Emo name="egg" />} size={size} onSize={onSize} onFullPage={onFullPage} onClose={onClose}
        title={<>{ct('production')}{headerPlanned && <span className="chip info prodf-1"><Emo name="calendar" /> {t('dataEntry:fl.planned')}</span>}</>} />

      <div className="dr-body">
        <div className="mrtoggle">
          <label><input type="checkbox" checked={multiRow} onChange={e => { setMultiRow(e.target.checked); touch(); }} /> {t('dataEntry:fl.multi_row_entry')} <span className="mh">{t('dataEntry:fl.record_many_rows_at_once')}</span></label>
          {multiRow && <label className="sub-tog"><input type="checkbox" checked={oneBand} onChange={e => { setOneBand(e.target.checked); touch(); }} /> {t('dataEntry:fl.one_band_for_all_rows')} <span className="mh">{oneBand ? t('deUi.oneBandHeaderForEvery') : t('deUi.aBandPerRow')}</span></label>}
        </div>
        {msg && <div className={err ? 'err-banner' : 'hint ok-banner'}>{emoText(msg)}</div>}

        {!multiRow ? (
          <div className="form">
            <div className="field"><label>{t('fields.date', { defaultValue: 'Date' })}<span className="req">*</span></label>
              <TextInput type="date" value={form.date} onChange={set('date')} required /></div>
            <div className={'field' + (needsBand ? '' : ' grey')}><label>{needsBand ? t('fields.band', { defaultValue: 'Band' }) : t('fields.bandNotRequired', { defaultValue: 'Band (not required)' })}{needsBand && <span className="req">*</span>}</label>
              <BandPick bands={bands} value={form.band_id} onChange={v => set('band_id')(ev(v))} required={needsBand} disabled={!needsBand}
                placeholder={needsBand ? t('select.band', { defaultValue: 'Select band…' }) : t('select.noBand', { defaultValue: 'No band' })} /></div>
            <div className="field"><label>{t('fields.item', { defaultValue: 'Item' })}<span className="req">*</span></label>{itemSelect(form.item_id, pickItem, form.storage_id)}</div>
            <div className="field num"><label>{t('fields.quantity', { defaultValue: 'Quantity' })}<span className="req">*</span></label>
              <TextInput type="number" step="1" min="0" placeholder="0" value={form.qty} onChange={e => { const v = e.target.value; setForm(p => ({ ...p, qty: sq(v, p.item_id) })); touch(); }} required /></div>
            <div className="field"><label>{t('fields.storage', { defaultValue: 'Storage' })}<span className="req">*</span></label>{storageSelect(form.storage_id, set('storage_id'), form.item_id)}</div>
            <div className="field full"><label>{t('fields.notes', { defaultValue: 'Notes' })}</label><textarea value={form.notes} onChange={set('notes')} placeholder={t('dataEntry:fl.optional_note')} /></div>
            <ReferenceField className="field full" value={form.reference_id} onChange={v => { setForm(p => ({ ...p, reference_id: v })); touch(); }} />
          </div>
        ) : (
          <>
            <div className="form">
              <div className="field"><label>{t('fields.date', { defaultValue: 'Date' })}<span className="req">*</span></label>
                <TextInput type="date" value={mDate} onChange={e => { setMDate(e.target.value); touch(); }} required /></div>
              {oneBand && <div className="field"><label>{t('fields.band', { defaultValue: 'Band' })}<span className="req">*</span></label>
                <BandPick bands={bands} value={mBand} onChange={v => { setMBand(v); touch(); }} required placeholder={t('select.band', { defaultValue: 'Select band…' })} /></div>}
            </div>
            <div><div className="sec-lab">{t('dataEntry:fl.rows')}</div>
              <div className="gtable-wrap"><table className="gtable zebra">
                <thead><tr><th className="act-col" />{!oneBand && <th className="prodf-2">{t('dataEntry:fields.band')}</th>}<th className="prodf-3">{t('dataEntry:fields.item')}</th><th className="ctr prodf-4">{t('dataEntry:placeholder.qty')}</th><th className="prodf-5">{t('dataEntry:fields.storage')}</th><th>{t('dataEntry:fields.notes')}</th></tr></thead>
                <tbody>
                  {rows.map((r, i) => (
                    <tr key={i} className={r._status && r._status.state === 'fail' ? 'row-fail' : undefined}><td className="act-col"><button type="button" className="tool" title={t('dataEntry:fl.remove_row')} onClick={() => dropRow(i)} disabled={rows.length === 1}><IcoLn name="close" /></button></td>
                      {!oneBand && <td><BandPick bands={bands} value={r.band_id} onChange={v => patch(i, { band_id: v })} /></td>}
                      <td>{itemSelect(r.item_id, e => { const v = e.target.value, it = items.find(x => x.id === v); patch(i, { item_id: v }); void it; }, r.storage_id)}</td>
                      <td className="num ctr"><TextInput dense type="number" step="1" min="0" placeholder="0" value={r.qty} onChange={e => patch(i, { qty: sq(e.target.value, r.item_id) })} /></td>
                      <td>{storageSelect(r.storage_id, e => patch(i, { storage_id: e.target.value }), r.item_id)}</td>
                      <td><TextInput dense type="text" value={r.notes} placeholder="—" onChange={e => patch(i, { notes: e.target.value })} /></td>
                      
                    </tr>
                  ))}
                </tbody>
              </table></div>
              <button type="button" className="addline" onClick={addRow}>{t('dataEntry:actions.addRow')}</button>
            </div>
            {vWarn && <div className="warn"><div className="wt"><Emo name="warn" /> {t('dataEntry:fl.batch_has_warnings')}</div>{t('dataEntry:fl.the_engine_flagged_warnings_on_this_batch')}<label><input type="checkbox" checked={ack} onChange={e => { setAck(e.target.checked); touch(); }} /> {t('dataEntry:fl.i_acknowledge_the_warnings_and_want_to')}</label></div>}
          </>
        )}

        <div className="hint">{t('dataEntry:fl.hint_production')}{multiRow ? t('dataEntry:fl.hint_multirow_suffix') : ''}</div>
      </div>

      <DrawerFooter
         onClose={onClose}
         start={<button type="button" className="btn" disabled={busy} onClick={clearForm}>{al('clearForm')}</button>}
         secondary={<><button type="button" className="btn" disabled={busy || invalid} onClick={() => doSave(true, false)}>{al('saveAsPlanned')}</button><button type="button" className="btn" disabled={busy || invalid} onClick={() => doSave(false, true)}>{al('saveKeepPrefill')}</button></>}
         primary={<button type="button" className="btn success" disabled={busy || invalid || headerPlanned} onClick={() => doSave(false, false)}>{busy ? t('dataEntry:state.saving') : t('dataEntry:fl.save_record')}</button>} />
      {headerPlanned && <div className="futixnote"><Emo name="calendar" /> {t('dataEntry:fl.future_date_can_only_be_saved_as')} <b>{t('dataEntry:fl.planned_2')}</b>.</div>}
    </>
  );
}
